import type { CSSProperties } from 'react';
import { useCallback } from 'react';

import { useBoardMembers } from '@trello/business-logic-react/board';
import { smallestPreviewBiggerThan } from '@trello/image-previews';
import { localizeCount } from '@trello/legacy-i18n';
import { PrivateIcon } from '@trello/nachos/icons/private';
import { navigate } from '@trello/router/navigate';
import { token } from '@trello/theme';

import { RemoveWorkspaceCollaboratorFromBoardButton } from './RemoveWorkspaceCollaboratorFromBoardButton';
import { useWorkspaceBoardsListItemFragment } from './WorkspaceBoardsListItemFragment.generated';

import styles from './WorkspaceBoardsListItem.less';

export function WorkspaceBoardsPrivateBoardSummaryItem({
  privateBoardCount,
}: {
  privateBoardCount: number;
}) {
  if (privateBoardCount === 0) {
    return null;
  }

  return (
    <li className={styles.privateBoardSummary}>
      <div className={styles.privateBoardThumbnail}>
        <PrivateIcon color={token('color.icon.accent.red', '#C9372C')} />
      </div>
      <div className={styles.privateBoardTitle}>
        {localizeCount('private-boards', privateBoardCount)}
      </div>
    </li>
  );
}

interface WorkspaceBoardsListItemProps {
  idBoard: string;
  idMember: string;
  idOrganization: string;
}

export function WorkspaceBoardsListItem({
  idBoard,
  idMember,
  idOrganization,
}: WorkspaceBoardsListItemProps) {
  const { canRemoveBoardMember } = useBoardMembers(idBoard);

  const { data: board } = useWorkspaceBoardsListItemFragment({
    from: { id: idBoard },
    optimistic: true,
  });

  const boardName = board?.name;

  const onClickBoardName = useCallback(() => {
    navigate(`b/${board?.shortLink}`, { trigger: true });
  }, [board?.shortLink]);

  const backgroundStyle: CSSProperties = {};

  if (board?.prefs?.backgroundImageScaled) {
    const image = smallestPreviewBiggerThan(
      board.prefs.backgroundImageScaled,
      24,
      24,
    );
    if (image) {
      backgroundStyle.backgroundImage = `url('${image.url}')`;
    }
  }

  if (board?.prefs?.backgroundTopColor) {
    backgroundStyle.backgroundColor = board.prefs.backgroundTopColor;
  }

  return (
    <a
      role="button"
      className={styles.boardsListItem}
      onClick={onClickBoardName}
    >
      <div className={styles.boardThumbnail} style={backgroundStyle} />
      <div className={styles.boardName}>{board?.name}</div>
      {canRemoveBoardMember(idMember) && (
        <RemoveWorkspaceCollaboratorFromBoardButton
          idMember={idMember}
          idOrganization={idOrganization}
          boardName={boardName}
          idBoard={idBoard}
        />
      )}
    </a>
  );
}
